/// <amd-module name="DistributedTaskUI/LogReader/SubParser" />
import { IFindResult } from "./Types";

/// <amd-module name="DistributedTaskUI/LogReader/AnsiParser" />
// DUP of DistributedTaskUI/Logs/AnsiParser, eventually DistributedTaskUI/Logs/AnsiParser should go away, this will be the latest

const ESC = "\u001b";
/**
 * Regex to identify the parsed lines
 */
export const RegexToIdentify = /\u200C/;

/**
 * Regex for matching ANSII escape codes
 * \u001b - ESC character
 * ?: Non-capturing group
 * (?:\u001b[) : Match ESC[
 * (?:[\?|#])??: Match also ? and # formats that we don't supports but want to eat our special characters to get rid of ESC character
 * (?:[0-9]{1,3})?: Match one or more occurances of the simple format we want with out semicolon
 * (?:(?:;[0-9]{0,3})*)?: Match one or more occurances of the format we want with semicolon
 */
const _ansiEscapeCodeRegex = /(?:\u001b\[)(?:[\?|#])?(?:(?:[0-9]{1,3})?(?:(?:;[0-9]{0,3})*)?[A-Z|a-z])/;
export const ansiEscapeCodeRegexString = "(?:\u001b\\[)(?:[\?|#])?(?:(?:[0-9]{1,3})?(?:(?:;[0-9]{0,3})*)?[A-Z|a-z])";

/**
 * http://ascii-table.com/ansi-escape-sequences.php
 * We support sequences of format:
 *  Esc[CONTENTHEREm
 *  Where CONTENTHERE can be of format: VALUE;VALUE;VALUE or VALUE
 *      Where VALUE is SGR parameter https://www.vt100.net/docs/vt510-rm/SGR
 *          We support: 0 (reset), 1 (bold), 3 (italic), 4 (underline), 22 (not bold), 23 (not italic), 24 (not underline), 39 (default fg), 49 (default bg), 
 *                      fg colors - 30 (black), 31 (red), 32 (green), 33 (yellow), 34 (blue), 35 (magenta), 36 (cyan), 37 (white), 90 (grey)
 *                      bg colors - 40 (black), 41 (red), 42 (green), 43 (yellow), 44 (blue), 45 (magenta), 46 (cyan), 47 (white), 
 *  Where m refers to the "Graphics mode"
 */

enum Resets {
    Reset = "0",
    Bold = "22",
    Italic = "23",
    Underline = "24",
    Fg = "39",
    Bg = "49"
}

const specials = {
    "1": "bold",
    "3": "italic",
    "4": "underline"
} as { [key: string]: string };

export const bgColors = {
    // 40 (black), 41 (red), 42 (green), 43 (yellow), 44 (blue), 45 (magenta), 46 (cyan), 47 (white), 
    "40": "b",
    "41": "r",
    "42": "g",
    "43": "y",
    "44": "bl",
    "45": "m",
    "46": "c",
    "47": "w"
} as { [key: string]: string };

export const fgColors = {
    // 30 (black), 31 (red), 32 (green), 33 (yellow), 34 (blue), 35 (magenta), 36 (cyan), 37 (white), 90 (grey)
    "30": "b",
    "31": "r",
    "32": "g",
    "33": "y",
    "34": "bl",
    "35": "m",
    "36": "c",
    "37": "w",
    "90": "gr"
} as { [key: string]: string };


export interface IStyle {
    fg: string;
    bg: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
    [key: string]: boolean | string;
}

export function getText(text: string) {
    return (text || "").toLocaleLowerCase();
}

interface IAnsiEscapeCodeState {
    output: string;
    style?: IStyle;
}

export class SubParser {
    /**
     * Converts the content to HTML with appropriate styles, escapes content to prevent XSS
     * @param content 
     * @param lineNumber
     * @param findResult
     */
    public parse(content: string, lineNumber: number, findResult?: IFindResult): string {
        let result = "";
        const states = this.getStates(content);
        states.forEach(x => {
            let classNames = "";
            if (x.style) {
                let fg = x.style.fg;
                let bg = x.style.bg;
                if (fg) {
                    classNames += `ansifg-${fg} `;
                }
                if (bg) {
                    classNames += `ansibg-${bg} `;
                }
                if (x.style.bold) {
                    classNames += "fontWeightHeavy ";
                }
                if (x.style.italic) {
                    classNames += " italic";
                }
                if (x.style.underline) {
                    classNames += " underline";
                }
            }

            let start = "<span";
            if (classNames) {
                start += ` class='${classNames}'`;
            }

            start += ">";

            let output = "";
            let matchIndex = -1;
            let prevMatchIndex = 0;
            if (findResult && (findResult.lines.indexOf(lineNumber) !== -1)) {
                const textToFind = findResult.text;
                /* tslint:disable no-conditional-assignment*/
                while ((matchIndex = getText(content).indexOf(getText(textToFind), matchIndex + 1)) !== -1) {
                    // unmatched before match
                    const preUnMatch = content.substring(prevMatchIndex, matchIndex);
                    if (preUnMatch) {
                        output += this._escape(preUnMatch);
                    }

                    prevMatchIndex = matchIndex + textToFind.length;
                    // actual match
                    const markerText = content.substring(matchIndex, prevMatchIndex);
                    output += `<span class="find-match ${lineNumber === findResult.toScroll ? "highlight" : ""}">` + this._escape(markerText) + "</span>";
                }
                /* tslint:enable no-conditional-assignment*/

                // rest of the unmatched content post all matches
                if (prevMatchIndex > 0 && content.length > prevMatchIndex) {
                    output += this._escape(content.substring(prevMatchIndex, content.length));
                }
            }
            else {
                output = this._escape(x.output);
            }

            result += start + output + "</span>";
        });

        return result;
    }

    public getStates(content: string): IAnsiEscapeCodeState[] {
        let result: IAnsiEscapeCodeState[] = [];
        // Eg: "ESC[0KESC[33;1mWorker informationESC[0m
        if (!_ansiEscapeCodeRegex.test(content)) {
            // Not of interest, don't touch content
            return [{
                output: content
            }];
        }

        let command = "";
        let currentText = "";
        let code = "";
        let state = {} as IAnsiEscapeCodeState;
        let isCommandActive = false;
        let codes = [];
        for (let index = 0; index < content.length; index++) {
            const character = content[index];
            if (isCommandActive) {
                if (character === ";") {
                    if (code) {
                        codes.push(code);
                        code = "";
                    }
                }
                else if (character === "m") {
                    if (code) {
                        isCommandActive = false;
                        // done
                        codes.push(code);
                        state.style = state.style || {} as IStyle;

                        codes.forEach(code => {
                            const style = state.style as IStyle;
                            if (fgColors[code]) {
                                style.fg = fgColors[code];
                            }
                            else if (bgColors[code]) {
                                style.bg = bgColors[code];
                            }
                            else if (code === Resets.Fg) {
                                style.fg = "";
                            }
                            else if (code === Resets.Bg) {
                                style.bg = "";
                            }
                            else if (specials[code]) {
                                style[specials[code]] = true;
                            }
                            else if (code === Resets.Bold) {
                                style.bold = false;
                            }
                            else if (code === Resets.Italic) {
                                style.italic = false;
                            }
                            else if (code === Resets.Underline) {
                                style.underline = false;
                            }
                        });

                        // clear
                        command = "";
                        currentText = "";
                        code = "";
                    }

                    codes = [];
                }
                else if (code.length === 2) {
                    // We probably got code that we don't support, ignore
                    code = "";
                    isCommandActive = false;
                    if (character !== ESC) {
                        // If this is not an ESC, let's not consider command from now on
                        // Eg: ESC[0Ksometexthere, at this point, code would be 0K, character would be 's'
                        command = "";
                        currentText += character;
                    }
                }
                else {
                    code += character;
                }

                continue;
            }
            else if (command) {
                if (command === ESC && character === "\[") {
                    isCommandActive = true;
                    // push state
                    if (currentText) {
                        state.output = currentText;
                        result.push(state);
                        state = {} as IAnsiEscapeCodeState;
                        currentText = "";
                    }
                }

                continue;
            }

            if (character === ESC) {
                command = character;
            }
            else {
                currentText += character;
            }
        }

        // still pending text
        if (currentText) {
            state.output = currentText + (command ? command : "");
            result.push(state);
        }

        return result;
    }

    //https://www.owasp.org/index.php/XSS_(Cross_Site_Scripting)_Prevention_Cheat_Sheet#RULE_.231_-_HTML_Escape_Before_Inserting_Untrusted_Data_into_HTML_Element_Content
    private _escape(content: string): string {
        let result = "";
        for (let index = 0; index < content.length; index++) {
            const char = content.charAt(index);
            if (char === "&") {
                result += "&amp;";
            }
            else if (char === "<") {
                result += "&lt;";
            }
            else if (char === ">") {
                result += "&gt;";
            }
            else if (char === "\"") {
                result += "&quot;";
            }
            else if (char === "'") {
                result += "&#x27;";
            }
            else if (char === "/") {
                result += "&#x2F;";
            }
            else {
                result += char;
            }
        }

        return result;
    }
}
